import React, { useRef, useState } from 'react';

import { CaretRightOutlined, PauseOutlined, SoundOutlined } from '@ant-design/icons';
import { Slider } from 'antd';
import styled from 'styled-components';

import { SPEED_TO_LEAD_PLAYBACK_RATES, SPEED_TO_LEAD_PROGRESS_STEP_SECONDS } from './constants';
import useSeekableRecording from './useSeekableRecording';

const AudioControls = styled.div`
  position: relative;
  display: grid;
  height: 52px;
  box-sizing: border-box;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 18px 2px;
  background: var(--white);
`;

const HiddenAudio = styled.audio`
  display: none;
`;

const IconButton = styled.button`
  display: inline-flex;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 16px;

  &:hover {
    background: var(--lighter-gray);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: 2px;
  }
`;

const PlayButton = styled(IconButton)`
  border: 1px solid var(--line-gray);
  background: var(--white);
  font-size: 15px;
`;

const SpeedButton = styled.button`
  justify-self: start;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  font-family: var(--font-bold);
  font-size: 11px;

  &:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: 2px;
  }
`;

const MainControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const VolumeButton = styled(IconButton)`
  justify-self: end;
`;

const MutedSoundIcon = styled.span`
  position: relative;
  display: inline-flex;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 18px;
    height: 1px;
    background: currentColor;
    content: '';
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const ProgressContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 12px;
`;

const ProgressSlider = styled(Slider)`
  && {
    height: 12px;
    margin: 0;
    padding: 0;

    .ant-slider-rail,
    .ant-slider-track {
      top: 9px;
      height: 3px;
      border-radius: 0;
    }

    .ant-slider-rail {
      background: var(--line-gray);
    }

    .ant-slider-track {
      background: var(--active-blue);
    }

    .ant-slider-handle {
      top: 4px;
      opacity: 0;
    }

    &:hover .ant-slider-handle,
    .ant-slider-handle:focus-visible {
      opacity: 1;
    }
  }
`;

const PlaybackError = styled.div`
  padding: 8px 18px;
  color: var(--text-secondary);
  font-size: 12px;
`;

type Props = {
  eventId: string | number;
  source: string;
};

const SpeedToLeadTouchpointAudio = ({ eventId, source }: Props) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingSeekRef = useRef<{ seconds: number; resume: boolean } | null>(null);
  const { prepareRecording, hasSeekError } = useSeekableRecording(source);
  const [activeSource, setActiveSource] = useState(source);
  const [playbackRate, setPlaybackRate] = useState(SPEED_TO_LEAD_PLAYBACK_RATES[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const updateProgress = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const hasValidDuration = Number.isFinite(audio.duration) && audio.duration > 0;
    setDuration(hasValidDuration ? audio.duration : 0);
    if (!pendingSeekRef.current) setCurrentTime(audio.currentTime);
  };

  const cyclePlaybackRate = () => {
    const currentIndex = SPEED_TO_LEAD_PLAYBACK_RATES.indexOf(playbackRate);
    const nextRate = SPEED_TO_LEAD_PLAYBACK_RATES[(currentIndex + 1) % SPEED_TO_LEAD_PLAYBACK_RATES.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || isStarting) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }

    prepareRecording().catch(() => undefined);
    setIsStarting(true);
    setHasError(false);
    try {
      if (audio.error) audio.load();
      audio.playbackRate = playbackRate;
      audio.muted = isMuted;
      await audio.play();
    } catch {
      if (audioRef.current === audio) setHasError(true);
    } finally {
      if (audioRef.current === audio) setIsStarting(false);
    }
  };

  const seek = async (seconds: number) => {
    const audio = audioRef.current;
    if (!audio || !duration || hasError || isStarting) return;
    const targetTime = Math.max(0, Math.min(duration, seconds));
    const resume = !audio.paused;
    setIsStarting(true);
    try {
      const seekSource = await prepareRecording();
      if (audioRef.current !== audio) return;
      if (activeSource !== seekSource) {
        pendingSeekRef.current = { seconds: targetTime, resume };
        setCurrentTime(targetTime);
        setActiveSource(seekSource);
        return;
      }
      audio.currentTime = targetTime;
      setCurrentTime(audio.currentTime);
      setIsStarting(false);
    } catch {
      if (audioRef.current === audio) setIsStarting(false);
    }
  };

  const onLoadedMetadata = async () => {
    const audio = audioRef.current;
    const pendingSeek = pendingSeekRef.current;
    if (!audio || !pendingSeek) {
      updateProgress();
      return;
    }
    pendingSeekRef.current = null;
    try {
      audio.currentTime = Math.min(pendingSeek.seconds, audio.duration);
      audio.playbackRate = playbackRate;
      audio.muted = isMuted;
      updateProgress();
      if (pendingSeek.resume) await audio.play();
    } catch {
      if (audioRef.current === audio) setHasError(true);
    } finally {
      if (audioRef.current === audio) setIsStarting(false);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(audioRef.current.muted);
    }
  };

  return (
    <>
      <AudioControls>
        <HiddenAudio
          ref={audioRef}
          src={activeSource}
          preload="metadata"
          onLoadedMetadata={onLoadedMetadata}
          onDurationChange={updateProgress}
          onProgress={updateProgress}
          onTimeUpdate={updateProgress}
          onError={() => {
            pendingSeekRef.current = null;
            setHasError(true);
            setIsStarting(false);
            setIsPlaying(false);
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
        <SpeedButton
          type="button"
          onClick={cyclePlaybackRate}
          aria-label={`Playback speed ${playbackRate} times`}
          title="Change playback speed"
          data-test-id={`speed-to-lead-touchpoint-audio-speed-${eventId}`}
        >
          {playbackRate}X
        </SpeedButton>
        <MainControls>
          <PlayButton
            type="button"
            onClick={togglePlayback}
            disabled={isStarting}
            aria-busy={isStarting}
            aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
            title={isPlaying ? 'Pause' : 'Play'}
            data-test-id={`speed-to-lead-touchpoint-audio-play-${eventId}`}
          >
            {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
          </PlayButton>
        </MainControls>
        <VolumeButton
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute recording' : 'Mute recording'}
          title={isMuted ? 'Unmute' : 'Mute'}
          data-test-id={`speed-to-lead-touchpoint-audio-volume-${eventId}`}
        >
          {isMuted ? (
            <MutedSoundIcon>
              <SoundOutlined />
            </MutedSoundIcon>
          ) : (
            <SoundOutlined />
          )}
        </VolumeButton>
        <ProgressContainer data-test-id={`speed-to-lead-touchpoint-audio-progress-${eventId}`}>
          <ProgressSlider
            min={0}
            max={duration || 0}
            step={SPEED_TO_LEAD_PROGRESS_STEP_SECONDS}
            disabled={!duration || hasSeekError || hasError || isStarting}
            value={currentTime}
            onChange={seek}
            tooltip={{ open: false }}
            ariaLabelForHandle="Recording progress"
          />
        </ProgressContainer>
      </AudioControls>
      {hasError && <PlaybackError role="alert">Recording could not be played. Please try again.</PlaybackError>}
      {hasSeekError && (
        <PlaybackError role="alert">Seeking is unavailable. Please reopen the touchpoint to retry.</PlaybackError>
      )}
    </>
  );
};

export default SpeedToLeadTouchpointAudio;
